import { useState, useCallback, useEffect, useMemo } from 'react'
import type { JSX } from 'react'

import {
  Avatar,
  Badge,
  Button,
  Dialog,
  Flex,
  Heading,
  Label,
  Skeleton,
  Switch,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { useRefreshOwnAccess } from '../../../shared/hooks/useRefreshOwnAccess'
import {
  useAssignableChannels,
  useUpdateTeamMemberRole,
  useUpdateChannelPermissions,
} from '../../hooks'
import type { TeamMember } from '../../hooks'
import type { AccessLevel, ProductAccessLevels, ProductKey } from '../../types'
import { getDisplayName } from '../../utils/getDisplayName'
import {
  NO_ACCESS,
  areAllProductLevelsUniform,
  areProductLevelsEqual,
  buildPerChannelGrants,
} from '../../utils/productAccessLevels'
import { ChannelPermissionsSection } from '../ChannelPermissions'

import styles from './EditMemberDialog.module.css'

interface EditMemberDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
  member: TeamMember | null
}

export const EditMemberDialog = ({
  isOpen,
  onOpenChange,
  onClose,
  member,
}: EditMemberDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const accountId = useAccountId()
  const refreshOwnAccess = useRefreshOwnAccess()

  const [isAdmin, setIsAdmin] = useState(false)
  const [hasRoleChanged, setHasRoleChanged] = useState(false)

  // Channel access: a global pair (collapsed) plus per-channel grants
  // (expanded). Unlike the invite dialog the overrides are always seeded from
  // the member's existing grants so an already-mixed member renders correctly.
  const [globalLevels, setGlobalLevels] =
    useState<ProductAccessLevels>(NO_ACCESS)
  const [overrides, setOverrides] = useState<Map<string, ProductAccessLevels>>(
    new Map(),
  )
  const [initialOverrides, setInitialOverrides] = useState<
    Map<string, ProductAccessLevels>
  >(new Map())
  const [isCustomized, setIsCustomized] = useState(false)

  const { assignableChannels, loading: loadingChannels } =
    useAssignableChannels({
      organizationId: organizationId ?? '',
      accountId: member?.id,
    })

  const { updateTeamMemberRole, loading: updatingRole } =
    useUpdateTeamMemberRole()
  const { updateChannelPermissions, loading: updatingChannels } =
    useUpdateChannelPermissions()

  // Initialize form state when the member or the channel list changes. The
  // channel list is needed so every channel — including those the member has
  // no grant on — gets a row.
  useEffect(() => {
    if (!member) return

    setIsAdmin(member.organizationRole === 'admin')
    setHasRoleChanged(false)

    // Reset the baseline up front so a reused dialog never compares against —
    // or submits — the previous member's grants while this member's channels
    // are still loading. Re-seeded below once they resolve.
    setGlobalLevels(NO_ACCESS)
    setOverrides(new Map())
    setInitialOverrides(new Map())
    setIsCustomized(false)

    if (assignableChannels) {
      const perChannel = buildPerChannelGrants(
        member.channelPermissions,
        assignableChannels.map(({ channel }) => channel.id),
      )
      setOverrides(perChannel)
      setInitialOverrides(new Map(perChannel))

      // Uniform members open collapsed with the shared pair; any variance opens
      // the per-channel table so the existing mix isn't misrepresented.
      const levels = Array.from(perChannel.values())
      const uniform = areAllProductLevelsUniform(levels)
      setGlobalLevels(levels[0] ?? NO_ACCESS)
      setIsCustomized(!uniform)
    }
  }, [member, assignableChannels])

  const handleClose = onClose

  // Route every dismissal (Cancel, Escape, backdrop) through `handleClose`.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true)
      } else {
        handleClose()
      }
    },
    [onOpenChange, handleClose],
  )

  const handleRoleChange = useCallback(
    (checked: boolean) => {
      setIsAdmin(checked)
      setHasRoleChanged(checked !== (member?.organizationRole === 'admin'))
    },
    [member],
  )

  // Collapsed: update one product's level. Mirror into overrides so a later
  // Customize starts from the global pair.
  const handleGlobalLevelChange = useCallback(
    (product: ProductKey, level: AccessLevel) => {
      setGlobalLevels((prev) => ({ ...prev, [product]: level }))
    },
    [],
  )

  // Expanded: update one product's level on a single channel.
  const handleOverrideChange = useCallback(
    (channelId: string, product: ProductKey, level: AccessLevel) => {
      setOverrides((prev) => {
        const next = new Map(prev)
        const current = next.get(channelId) ?? globalLevels
        next.set(channelId, { ...current, [product]: level })
        return next
      })
    },
    [globalLevels],
  )

  // Customize: seed every channel's overrides from the global pair, then expand.
  const handleCustomize = useCallback(() => {
    if (!assignableChannels) return
    setOverrides(
      new Map(
        assignableChannels.map(({ channel }) => [
          channel.id,
          { ...globalLevels },
        ]),
      ),
    )
    setIsCustomized(true)
  }, [assignableChannels, globalLevels])

  // Reset: collapse back to the global pair (drops per-channel variance).
  const handleResetCustomization = useCallback(() => {
    if (!assignableChannels) return
    setOverrides(
      new Map(
        assignableChannels.map(({ channel }) => [
          channel.id,
          { ...globalLevels },
        ]),
      ),
    )
    setIsCustomized(false)
  }, [assignableChannels, globalLevels])

  // The effective per-channel grants given the current collapsed/expanded
  // state: collapsed applies the global pair to every channel.
  const effectiveGrants = useCallback((): Map<string, ProductAccessLevels> => {
    if (!assignableChannels) return new Map()
    return new Map(
      assignableChannels.map(({ channel }) => [
        channel.id,
        isCustomized
          ? (overrides.get(channel.id) ?? globalLevels)
          : globalLevels,
      ]),
    )
  }, [assignableChannels, isCustomized, overrides, globalLevels])

  // Whether the grants differ from what was loaded. While the member's
  // channels are still resolving there's no meaningful baseline to compare
  // against, so report "no change" rather than acting on a stale one.
  const hasChannelsChanged = useMemo((): boolean => {
    if (!assignableChannels) return false
    const current = effectiveGrants()
    if (current.size !== initialOverrides.size) return true
    for (const [channelId, levels] of current) {
      const initial = initialOverrides.get(channelId) ?? NO_ACCESS
      if (!areProductLevelsEqual(levels, initial)) return true
    }
    return false
  }, [assignableChannels, effectiveGrants, initialOverrides])
  const hasChanges = hasRoleChanged || hasChannelsChanged

  const handleSubmit = async (): Promise<void> => {
    if (!organizationId || !member) return

    if (!hasChanges) {
      handleClose()
      return
    }

    let success = true
    const errors: string[] = []

    try {
      if (hasRoleChanged) {
        const roleResult = await updateTeamMemberRole({
          organizationId,
          accountId: member.id,
          organizationRole: isAdmin ? 'admin' : 'member',
        })
        if (!roleResult.success) {
          success = false
          errors.push(roleResult.error ?? 'Failed to update role')
        }
      }

      if (hasChannelsChanged) {
        // Send every channel with both products; the backend drops grants
        // whose every product is `none`.
        const productChannelAssignments = Array.from(
          effectiveGrants().entries(),
        ).map(([channelId, productAccessLevels]) => ({
          channelId,
          productAccessLevels,
        }))
        const channelResult = await updateChannelPermissions({
          organizationId,
          accountId: member.id,
          productChannelAssignments,
        })
        if (!channelResult.success) {
          success = false
          errors.push(channelResult.error ?? 'Failed to update channels')
        }
      }

      if (success) {
        toast.success('Team member updated successfully')
        handleClose()
        // An admin editing their own role or channel access invalidates most
        // of what's cached for them (sidebar channels, Community actions,
        // user menu) — refresh it all rather than let it go stale.
        if (member.id === accountId) {
          void refreshOwnAccess()
        }
      } else {
        toast.error(errors.join('. '))
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      errors.push(errorMessage)
      toast.error(errors.join('. '))
    }
  }

  const isSaving = updatingRole || updatingChannels

  if (!member) return <></>

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Header>
          <Dialog.Title>Edit Team Member</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className={styles.form}>
            {/* Member info header */}
            <Flex className={styles.memberHeader} align="center" gap="md">
              <Avatar
                src={member.account?.avatar}
                alt={getDisplayName(member)}
                className={styles.avatar}
              />
              <Flex direction="column">
                <Flex align="center" gap="xs">
                  <Heading size="xs">{getDisplayName(member)}</Heading>
                  {member.isOwner && (
                    <Badge size="medium" color="brand">
                      Owner
                    </Badge>
                  )}
                </Flex>
                <Text color="subtle">{member.email}</Text>
              </Flex>
            </Flex>

            {/* Admin toggle */}
            <Flex align="center" justify="between" gap="md">
              <Flex direction="column" gap="2xs">
                <Label htmlFor="admin-toggle">Admin</Label>
                <Text color="subtle" size="sm">
                  Can manage team members and team settings
                </Text>
              </Flex>
              <Switch
                id="admin-toggle"
                checked={isAdmin}
                onCheckedChange={handleRoleChange}
                disabled={member.isOwner}
              />
            </Flex>

            {/* Channel access. The two-column section carries its own
                header. */}
            {loadingChannels ? (
              <Flex
                direction="column"
                gap="xs"
                className={styles.channelListLoading}
                fullWidth
              >
                <Skeleton height="48px" width="100%" />
                <Skeleton height="48px" width="100%" />
              </Flex>
            ) : assignableChannels?.length ? (
              <ChannelPermissionsSection
                channels={assignableChannels}
                globalLevels={globalLevels}
                overrides={overrides}
                isCustomized={isCustomized}
                onGlobalChange={handleGlobalLevelChange}
                onOverrideChange={handleOverrideChange}
                onCustomize={handleCustomize}
                onReset={handleResetCustomization}
              />
            ) : (
              <Text color="subtle">No channels available</Text>
            )}
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving}
            loading={isSaving}
          >
            Save Changes
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
